import React, { useMemo, useContext, useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { ChangePasswordTab, InvitesTab } from 'components/SettingsTabs'
import 'pages/Settings/Settings.scss'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import DevelopersTab from 'components/SettingsTabs/DevelopersTab'
import slugify from 'utils/slugify'
import Destinations from 'components/SettingsTabs/Destinations/Destinations'
import BookmarkSettings from 'components/Bookmarks/BookmarkSettings'
import { useFeatureCtx } from 'containers/features/FeatureContext'

// New account settings tabs
import ProfileTab from './tabs/ProfileTab'
import LoginProvidersTab from './tabs/LoginProvidersTab'
import DevicesTab from './tabs/DevicesTab'
import SecurityTab from './tabs/SecurityTab'
import McpSettingsTab from './tabs/McpSettingsTab'
// ETL Settings tab hidden — not production ready, may be re-enabled in the future
// import EtlSettingsTab from './tabs/EtlSettingsTab'
import PreferencesTab from './tabs/PreferencesTab'
import SubscriptionsTab from './tabs/SubscriptionsTab'
import OrganizationTab from './tabs/OrganizationTab'

const TABS = [
  // Account Settings Tabs
  {
    name: 'Profile',
    slug: slugify('Profile'),
    settingType: 'account',
  },
  {
    name: 'Login Providers',
    slug: slugify('Login Providers'),
    settingType: 'account',
  },
  {
    name: 'Devices & Sessions',
    slug: slugify('Devices & Sessions'),
    settingType: 'account',
  },
  {
    name: 'Security',
    slug: slugify('Security'),
    settingType: 'account',
  },
  {
    name: 'Preferences',
    slug: slugify('Preferences'),
    settingType: 'account',
  },
  {
    name: 'MCP Settings',
    slug: slugify('MCP Settings'),
    settingType: 'account',
  },
  // Administration Tabs
  { name: 'Organization', slug: slugify('Organization'), settingType: 'administration' },
  { name: 'Subscriptions', slug: slugify('Subscriptions'), settingType: 'administration' },
  { name: 'Members', slug: slugify('Members'), settingType: 'administration' },
  // ETL Settings tab hidden — not production ready, may be re-enabled in the future
  // {
  //   name: 'ETL Settings',
  //   slug: slugify('ETL Settings'),
  //   settingType: 'administration',
  // },
  {
    name: 'Developers',
    slug: slugify('Developers'),
    settingType: 'administration',
    feature: 'developers_tab' as const,
  },
  {
    name: 'Destinations',
    slug: slugify('Destinations'),
    settingType: 'administration',
    feature: 'destinations_tab' as const,
  },
  {
    name: 'Bookmarks',
    slug: slugify('Bookmarks'),
    settingType: 'administration',
    feature: 'bookmarks' as const,
  },
]

const Settings = ({ settingType }) => {
  const { currentOrganization } = useContext(OrganizationsContext)
  const { features } = useFeatureCtx()

  // Filter tabs based on settingType and feature flags
  const availableTabs = TABS.filter(
    (tab) =>
      tab.settingType === settingType &&
      (!tab.feature || (features && features[tab.feature]))
  )

  const tab =
    useParams<{ tab: string }>().tab ||
    availableTabs[0]?.slug ||
    ''
  const CurrentTab = useMemo(() => {
    switch (tab) {
      // Account Settings
      case 'profile':
        return <ProfileTab />
      case 'login-providers':
        return <LoginProvidersTab />
      case 'devices-sessions':
        return <DevicesTab />
      case 'security':
        return <SecurityTab />
      case 'mcp-settings':
        return <McpSettingsTab />
      // ETL Settings tab hidden — not production ready
      // case 'etl-settings':
      //   return <EtlSettingsTab />
      case 'preferences':
        return <PreferencesTab />
      // Administration Settings
      case 'organization':
        return <OrganizationTab />
      case 'subscriptions':
        return <SubscriptionsTab />
      case 'members':
        return <InvitesTab />
      case 'developers':
        return (
          <DevelopersTab
            organizationSlug={currentOrganization.slug}
          />
        )
      case 'destinations':
        return (
          <Destinations currentOrganization={currentOrganization}/>
        )
      case 'bookmarks':
        return (
          <BookmarkSettings currentOrganization={currentOrganization} />
        )
      default:
        return settingType === 'account' ? <ProfileTab /> : <InvitesTab />
    }
  }, [tab, currentOrganization.slug, settingType])

  return (
    <div className="organization-settings-page">
      <div className="head">
        <div className="page-name">
          <h2>
            {settingType.slice(0, 1).toUpperCase() + settingType.slice(1)}
          </h2>
          <span className="subtitle-text">
            {settingType === 'account'
              ? 'Manage your profile, security, and preferences.'
              : 'Invite new members, share your database or use the API key.'}
          </span>
        </div>
      </div>
      <TabNavigationMenu
        tabs={availableTabs}
        currentTab={tab}
        organizationSlug={currentOrganization.slug}
        settingType={settingType}
      />
      {CurrentTab && CurrentTab}
    </div>
  )
}

export default React.memo(Settings)

function TabNavigationMenu({
  tabs,
  currentTab,
  organizationSlug,
  settingType,
}) {
  const history = useHistory()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  if (currentTab === '') {
    history.push(
      `/organization/${organizationSlug}/${
        settingType === 'account' ? 'account-' : ''
      }settings/`
    )
  }

  function handleTabChange(e) {
    history.push(
      `/organization/${organizationSlug}/${
        settingType === 'account' ? 'account-' : ''
      }settings/${slugify(e.target.textContent)}`
    )
  }

  function handleDropdownChange(e) {
    const selectedTab = e.target.value
    history.push(
      `/organization/${organizationSlug}/${
        settingType === 'account' ? 'account-' : ''
      }settings/${selectedTab}`
    )
  }

  const currentTabName = tabs.find(tab => tab.slug === currentTab)?.name || tabs[0]?.name

  if (isMobile) {
    return (
      <div className="mobile-tab-selector">
        <div className="selector-wrapper">
          <select
            value={currentTab}
            onChange={handleDropdownChange}
            className="tab-select"
          >
            {tabs?.map((tab) => (
              <option key={tab.slug} value={tab.slug}>
                {tab.name}
              </option>
            ))}
          </select>
          <span className="material-icons dropdown-icon">keyboard_arrow_down</span>
        </div>
      </div>
    )
  }

  return (
    <nav className="page-navigation-menu">
      <ul>
        {tabs?.map(
          (tab) =>
            tab.settingType === settingType && (
              <li
                key={tab.slug}
                onClick={handleTabChange}
                className={tab.slug === currentTab ? 'selected' : ''}
              >
                {tab.name}
              </li>
            )
        )}
      </ul>
    </nav>
  )
}
